/**
 * Data Processing Utilities
 * Pure functions for data transformations and calculations
 */

/**
 * Normalize a string for accent-insensitive search.
 * "balú" and "balu" both become "balu".
 */
export function normalizeText(str) {
    return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Normalize dog owners to always be an array
 * @param {Array} dogs - Array of dogs to normalize
 * @returns {Array} Normalized dogs array
 */
export function normalizeDogOwners(dogs) {
    return dogs.map(dog => ({
        ...dog,
        owners: Array.isArray(dog.owners)
            ? dog.owners
            : dog.owner
                ? [dog.owner]
                : []
    }));
}

/**
 * Paginate an array
 * @param {Array} items - Items to paginate
 * @param {number} currentPage - Current page number (1-based)
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Pagination result with items and metadata
 */
export function paginateArray(items, currentPage, itemsPerPage) {
    const totalPages = Math.ceil(items.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = items.slice(startIndex, endIndex);

    return {
        items: paginatedItems,
        totalPages,
        currentPage,
        totalItems: items.length,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1
    };
}

/**
 * Transform dog data for API submission
 * @param {Object} formData - Form data
 * @returns {Object} Transformed data for API
 */
export function transformDogFormData({ name, breed, age, weight, particularSigns, primaryOwnerId, secondaryOwnerId }) {
    // Create owners array, filtering out empty/null values
    const owners = [];
    if (primaryOwnerId && primaryOwnerId.trim()) {
        owners.push(primaryOwnerId.trim());
    }
    if (secondaryOwnerId && secondaryOwnerId.trim()) {
        owners.push(secondaryOwnerId.trim());
    }
    
    const transformed = {
        name: name.trim(),
        breed: breed.trim(),
        age: parseInt(age, 10),
        owners
    };
    
    // Add optional fields only if they have values
    if (weight !== undefined && weight !== '') {
        transformed.weight = parseFloat(weight);
    }
    
    if (particularSigns !== undefined && particularSigns !== '') {
        transformed.particularSigns = particularSigns.trim();
    }
    
    return transformed;
}

/**
 * Map owner IDs to owner objects
 * @param {Array} ownerIds - Array of owner IDs
 * @param {Array} allOwners - Array of all available owners
 * @returns {Array} Array of owner objects
 */
export function mapOwnerIdsToObjects(ownerIds, allOwners) {
    if (!Array.isArray(ownerIds) || !Array.isArray(allOwners)) {
        console.warn('mapOwnerIdsToObjects: Invalid input', { ownerIds, allOwners });
        return [];
    }
    return allOwners.filter(owner => ownerIds.includes(owner._id));
}

